import { Button } from '@mui/material'
import type { ReactNode } from 'react'

import { Box } from '../Box'
import { Modal } from '../Modal'

export interface ConfirmModalProps {
  title: string
  children: ReactNode
  onConfirm: () => void
  onCancel: () => void
  confirmLabel?: string
  cancelLabel?: string
  /** Renders the confirm button in the error colour. */
  destructive?: boolean
}

/**
 * The presentational half of the confirmation flow.
 *
 * Built on `Modal` rather than its own `Dialog` so a confirmation inherits the
 * shell every other dialog in the app uses. `ConfirmProvider` owns the only
 * instance; components ask for a confirmation through `useConfirm` instead of
 * mounting this themselves.
 */
export const ConfirmModal = ({
  title,
  children,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
}: ConfirmModalProps) => (
  <Modal
    title={title}
    onClose={onCancel}
    maxWidth='sm'
    actions={
      <Box variant='flexBetween' fullWidth>
        <Button variant='text' onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant='contained'
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </Box>
    }
  >
    {children}
  </Modal>
)
